import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Search, AlertCircle } from 'lucide-react';
import EnhancedTable from '../../components/common/EnhancedTable';
import ExpandableText from '../../components/common/ExpandableText';
import { supabaseUnderLeaseService } from '../../services/supabase/underleases';
import type { UnderLease } from '../../types/underlease';
import { formatDate } from '../../utils/dateUtils';
import { formatTextWithLinks } from '../../utils/formatters';
import { TableColumn, SortConfig } from '../../components/common/EnhancedTable/types';
import '../../components/common/EnhancedTable/EnhancedTableFix.css';

type SortField = 'underlease_lease' | 'underlease_holder' | 'holder_name' | 'community_name' | 'lot_id' | 'underlease_start' | 'underlease_end' | 'underlease_use';

export default function UnderLeases() {
  const [searchQuery, setSearchQuery] = useState('');
  const [sortConfig, setSortConfig] = useState<SortConfig>({
    field: 'underlease_lease',
    direction: 'asc'
  });

  const { data: underleases, isLoading, error } = useQuery({
    queryKey: ['underleases'],
    queryFn: () => supabaseUnderLeaseService.getAll(),
    retry: 1
  });

  const filteredUnderleases = underleases?.filter(lease => {
    if (!searchQuery) return true;
    const searchLower = searchQuery.toLowerCase();
    return (
      lease.underlease_lease.toString().includes(searchLower) ||
      lease.underlease_holder.toString().includes(searchLower) ||
      lease.underlease_use.toLowerCase().includes(searchLower) ||
      (lease.holder_name || '').toLowerCase().includes(searchLower) ||
      (lease.community_name || '').toLowerCase().includes(searchLower) ||
      (lease.lot_id || '').toLowerCase().includes(searchLower)
    );
  });

  const sortedUnderleases = React.useMemo(() => {
    if (!filteredUnderleases) return [];
    
    return [...filteredUnderleases].sort((a, b) => {
      const direction = sortConfig.direction === 'asc' ? 1 : -1;
      
      switch (sortConfig.field) {
        case 'underlease_lease':
          return direction * (a.underlease_lease - b.underlease_lease);
        case 'underlease_holder':
          return direction * (a.underlease_holder - b.underlease_holder);
        case 'holder_name':
          return direction * ((a.holder_name || '').localeCompare(b.holder_name || ''));
        case 'community_name':
          return direction * ((a.community_name || '').localeCompare(b.community_name || ''));
        case 'lot_id':
          return direction * ((a.lot_id || '').localeCompare(b.lot_id || ''));
        case 'underlease_start':
          return direction * a.underlease_start.localeCompare(b.underlease_start);
        case 'underlease_end':
          return direction * a.underlease_end.localeCompare(b.underlease_end);
        case 'underlease_use':
          return direction * a.underlease_use.localeCompare(b.underlease_use);
        default:
          return 0;
      }
    });
  }, [filteredUnderleases, sortConfig]);

  const handleSort = (newSortConfig: SortConfig) => {
    setSortConfig(newSortConfig);
  };

  const columns: TableColumn<UnderLease>[] = [
    {
      id: 'underlease_lease',
      header: 'Lease ID',
      accessor: (row) => (
        <div className="truncate" title={row.underlease_lease.toString()}>
          {formatTextWithLinks(row.underlease_lease.toString())}
        </div>
      ),
      width: 'narrow',
      sortable: true
    },
    {
      id: 'lot_id',
      header: 'Lot ID',
      accessor: (row) => (
        <div className="truncate" title={row.lot_id || 'Not specified'}>
          {formatTextWithLinks(row.lot_id || 'Not specified')}
        </div>
      ),
      width: 'narrow',
      sortable: true
    },
    {
      id: 'community_name',
      header: 'Community',
      accessor: (row) => (
        <div className="truncate" title={row.community_name || 'Not specified'}>
          {formatTextWithLinks(row.community_name || 'Not specified')}
        </div>
      ),
      width: 'medium',
      sortable: true
    },
    {
      id: 'underlease_holder',
      header: 'Holder ID',
      accessor: (row) => (
        <div className="truncate" title={row.underlease_holder.toString()}>
          {formatTextWithLinks(row.underlease_holder.toString())}
        </div>
      ),
      width: 'narrow',
      sortable: true
    },
    {
      id: 'holder_name',
      header: 'Holder Name',
      accessor: (row) => (
        <div className="truncate" title={row.holder_name || 'Not specified'}>
          {formatTextWithLinks(row.holder_name || 'Not specified')}
        </div>
      ),
      width: 'medium',
      sortable: true
    },
    {
      id: 'underlease_start',
      header: 'Start Date',
      accessor: (row) => (
        <div className="truncate" title={formatDate(row.underlease_start)}>
          {formatDate(row.underlease_start)}
        </div>
      ),
      width: 'narrow',
      sortable: true
    },
    {
      id: 'underlease_end',
      header: 'End Date',
      accessor: (row) => (
        <div className="truncate" title={formatDate(row.underlease_end)}>
          {formatDate(row.underlease_end)}
        </div>
      ),
      width: 'narrow',
      sortable: true
    },
    {
      id: 'underlease_use',
      header: 'Use Type',
      accessor: (row) => (
        <ExpandableText 
          text={row.underlease_use || 'Not specified'} 
          maxLength={50} 
          className="text-sm"
        />
      ),
      width: 'wide',
      sortable: true
    }
  ];

  return (
    <div className="space-y-6 p-4 h-[calc(100vh-6rem)] overflow-y-auto">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Under-Leases</h1>
        <p className="mt-1 text-sm text-gray-500">
          View property under-leases
        </p>
      </div>

      <div className="relative">
        <input
          type="text"
          placeholder="Search under-leases..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:ring-primary focus:border-primary"
        />
        <Search className="absolute left-3 top-2.5 h-5 w-5 text-gray-400" />
      </div>

      {error ? (
        <div className="bg-white shadow-sm rounded-lg p-6">
          <div className="text-center">
            <div className="flex items-center justify-center gap-2 text-red-600">
              <AlertCircle className="h-5 w-5" />
              <p className="font-medium">Error loading under-lease data</p>
            </div>
            <p className="mt-1 text-sm text-gray-500">
              {error instanceof Error ? error.message : 'Please try again later'}
            </p>
            <button
              onClick={() => window.location.reload()}
              className="mt-4 px-4 py-2 bg-primary text-white rounded-md hover:bg-primary-dark"
            >
              Retry
            </button>
          </div>
        </div>
      ) : (
        <div className="bg-white shadow-sm rounded-lg overflow-hidden">
          <div className="overflow-x-auto" style={{ minWidth: '100%' }}>
            <EnhancedTable
              columns={columns}
              data={sortedUnderleases}
              keyField="underlease_lease"
              width="1500px"  // Standard width for tables with 5-7 columns
              maxHeight="calc(100vh - 16rem)"
              horizontalScroll={true}
              loading={isLoading}
              emptyMessage="No under-leases found"
              defaultSort={sortConfig}
              onSort={handleSort}
              className="enhanced-table-force-scroll"
            />
          </div>
        </div>
      )}
    </div>
  );
}
